import { DEFAULT_CURRENCY } from '../constants/appConstants';
import { mapCompanyClientTags, mapCompanyClientTagRules } from './clientTags';
import { mapCompanyCustomShapes } from './customShapes';
import { mapInventorySettingsToForm } from './inventorySettings';
import { mapSpecialRequestSettingsToForm } from './specialRequestSettings';

export const MANAGER_SETTINGS_SECTIONS = ['company', 'vat', 'currency', 'inventory', 'specialRequests', 'clientManagement', 'emailNotifications'];

export const CLIENT_MANAGEMENT_TABS = ['policies', 'tags', 'tagRules'];

export const INVENTORY_SETTINGS_TABS = ['lots', 'certified', 'shapes'];

export const CLIENT_MANAGEMENT_SUB_ITEMS = [
  { key: 'clientManagementPolicies', tab: 'policies', labelKey: 'settings.clientManagement.tabs.policies' },
  { key: 'clientManagementTags', tab: 'tags', labelKey: 'settings.clientManagement.tabs.tags' },
  { key: 'clientManagementTagRules', tab: 'tagRules', labelKey: 'settings.clientManagement.tabs.tagRules' },
];

export const INVENTORY_SETTINGS_SUB_ITEMS = [
  { key: 'inventorySettingsLots', tab: 'lots', labelKey: 'settings.inventory.tabs.lots' },
  { key: 'inventorySettingsCertified', tab: 'certified', labelKey: 'settings.inventory.tabs.certified' },
  { key: 'inventorySettingsShapes', tab: 'shapes', labelKey: 'settings.inventory.tabs.shapes' },
];

export const DEFAULT_EMAIL_NOTIFICATIONS = {
  enabled: false,
  orderReceived: false,
  orderReady: false,
  orderDelivered: false,
  paymentReceived: false,
  itemCancelled: false,
  orderCancelled: false,
};

export const DEFAULT_COMPANY_SETTINGS_FORM = {
  name: '',
  email: '',
  phone: '',
  address: { street: '', city: '', state: '', zipCode: '', country: '' },
  addresses: [],
  timeZone: 'UTC',
  vatPercentage: 0,
  pricesIncludeVat: false,
  defaultItemCurrency: DEFAULT_CURRENCY,
  checkCurrency: DEFAULT_CURRENCY,
  currencyRates: [],
  maxReservationAmount: '',
  maxDiscountPercent: '',
  maxExchangeRateChangeAmount: '',
  reservationMode: 'request',
  defaultReservationDuration: 7,
  emailNotifications: { ...DEFAULT_EMAIL_NOTIFICATIONS },
  clientTags: [],
  clientTagRules: [],
  customShapes: [],
  inventorySettings: mapInventorySettingsToForm({}).inventorySettings,
  ...mapSpecialRequestSettingsToForm({}),
};

export function shouldRedirectLanguageSection(section) {
  return section === 'language';
}

export function shouldRedirectClientPoliciesSection(section) {
  return section === 'clientPolicies';
}

export function getClientManagementPath(tab = 'policies') {
  return `/settings?section=clientManagement&tab=${tab}`;
}

export function getInventorySettingsPath(tab = 'lots') {
  return `/settings?section=inventory&tab=${tab}`;
}

export function isInventorySettingsRoute(search = '') {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return params.get('section') === 'inventory';
}

export function getInventorySettingsTab(search = '') {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const tab = params.get('tab') || 'lots';
  return INVENTORY_SETTINGS_TABS.includes(tab) ? tab : 'lots';
}

export function isInventorySettingsTabSelected(search, tab) {
  return isInventorySettingsRoute(search) && getInventorySettingsTab(search) === tab;
}

export function isClientManagementRoute(search = '') {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return params.get('section') === 'clientManagement';
}

export function getClientManagementTab(search = '') {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const tab = params.get('tab') || 'policies';
  return CLIENT_MANAGEMENT_TABS.includes(tab) ? tab : 'policies';
}

export function isClientManagementTabSelected(search, tab) {
  return isClientManagementRoute(search) && getClientManagementTab(search) === tab;
}

export function getSettingsProfilePath() {
  return '/settings?section=profile';
}

export function mapCompanyToForm(data, defaultCurrency = DEFAULT_CURRENCY) {
  return {
    name: data?.name || '',
    email: data?.email || '',
    phone: data?.phone || '',
    address: {
      street: data?.address?.street || '',
      city: data?.address?.city || '',
      state: data?.address?.state || '',
      zipCode: data?.address?.zipCode || '',
      country: data?.address?.country || '',
    },
    addresses: Array.isArray(data?.addresses) ? data.addresses : [],
    timeZone: data?.timeZone || 'UTC',
    vatPercentage: data?.vatPercentage ?? 0,
    pricesIncludeVat: data?.pricesIncludeVat === true,
    defaultItemCurrency: data?.defaultItemCurrency || defaultCurrency,
    checkCurrency: data?.checkCurrency || defaultCurrency,
    currencyRates: Array.isArray(data?.currencyRates) ? data.currencyRates : [],
    maxReservationAmount: data?.maxReservationAmount != null ? String(data.maxReservationAmount) : '',
    maxDiscountPercent: data?.maxDiscountPercent != null ? String(data.maxDiscountPercent) : '',
    maxExchangeRateChangeAmount: data?.maxExchangeRateChangeAmount != null
      ? String(data.maxExchangeRateChangeAmount)
      : '',
    reservationMode: data?.reservationMode || 'request',
    defaultReservationDuration: data?.defaultReservationDuration || 7,
    emailNotifications: {
      enabled: data?.emailNotifications?.enabled === true,
      orderReceived: data?.emailNotifications?.orderReceived === true,
      orderReady: data?.emailNotifications?.orderReady === true,
      orderDelivered: data?.emailNotifications?.orderDelivered === true,
      paymentReceived: data?.emailNotifications?.paymentReceived === true,
      itemCancelled: data?.emailNotifications?.itemCancelled === true,
      orderCancelled: data?.emailNotifications?.orderCancelled === true,
    },
    clientTags: mapCompanyClientTags(data),
    clientTagRules: mapCompanyClientTagRules(data),
    customShapes: mapCompanyCustomShapes(data),
    ...mapInventorySettingsToForm(data),
    ...mapSpecialRequestSettingsToForm(data),
  };
}

export function buildCompanySavePayload(form) {
  return {
    name: form.name,
    email: form.email || null,
    phone: form.phone || null,
    address: form.address,
    timeZone: form.timeZone || 'UTC',
  };
}

export function buildVatSavePayload(form) {
  return {
    vatPercentage: Number(form.vatPercentage) || 0,
    pricesIncludeVat: form.pricesIncludeVat,
  };
}

export function buildCurrencySavePayload(form) {
  const parsedMaxChange = form.maxExchangeRateChangeAmount === '' || form.maxExchangeRateChangeAmount == null
    ? null
    : parseFloat(form.maxExchangeRateChangeAmount);
  return {
    defaultItemCurrency: form.defaultItemCurrency,
    checkCurrency: form.checkCurrency,
    currencyRates: (form.currencyRates || []).map((row) => ({
      fromCurrency: row.fromCurrency,
      toCurrency: row.toCurrency || form.checkCurrency,
      rate: Number(row.rate),
    })),
    maxExchangeRateChangeAmount: parsedMaxChange,
  };
}

export function buildClientPolicySavePayload(form) {
  const parsed = form.maxReservationAmount === '' ? null : parseFloat(form.maxReservationAmount);
  const parsedDiscountCap = form.maxDiscountPercent === '' ? null : parseFloat(form.maxDiscountPercent);
  return {
    maxReservationAmount: parsed,
    maxDiscountPercent: parsedDiscountCap,
    reservationMode: form.reservationMode,
    defaultReservationDuration: Number(form.defaultReservationDuration) || 7,
  };
}

export function buildEmailNotificationsSavePayload(form) {
  return {
    emailNotifications: {
      enabled: form.emailNotifications?.enabled === true,
      orderReceived: form.emailNotifications?.orderReceived === true,
      orderReady: form.emailNotifications?.orderReady === true,
      orderDelivered: form.emailNotifications?.orderDelivered === true,
      paymentReceived: form.emailNotifications?.paymentReceived === true,
      itemCancelled: form.emailNotifications?.itemCancelled === true,
      orderCancelled: form.emailNotifications?.orderCancelled === true,
    },
  };
}

export function updateCurrencyRate(rates, index, key, value) {
  const next = [...(rates || [])];
  next[index] = { ...next[index], [key]: value };
  return next;
}

export function addCurrencyRate(rates, defaultItemCurrency, checkCurrency) {
  return [
    ...(rates || []),
    { fromCurrency: defaultItemCurrency, toCurrency: checkCurrency, rate: 1 },
  ];
}

export function removeCurrencyRate(rates, index) {
  return (rates || []).filter((_, i) => i !== index);
}

export function getSettingsNavSections(isManager) {
  const items = [{ section: 'profile' }];
  if (isManager) {
    items.push(
      { section: 'company' },
      { section: 'vat' },
      { section: 'currency' },
      { section: 'inventory' },
      { section: 'specialRequests' },
      { section: 'clientManagement' },
      { section: 'emailNotifications' },
    );
  }
  return items;
}
